/**
 * ExpenseReviewModal.js — просмотр расхода с фото чека перед согласованием.
 *
 * Открывается тапом по actionable-уведомлению «Расход на согласование» (слайс 3
 * демо). Показывает фото чека + распознанные ИИ данные, даёт ✓ согласовать / ✗ отклонить.
 * Логику согласования (реальный PATCH + события тура) держит вызывающий компонент.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';

export default function ExpenseReviewModal({ params, busy, onApprove, onReject, onClose }) {
  // Плавающая «+» прячется на время модала — общий механизм, см. useFabState.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('fab-hide'));
    return () => window.dispatchEvent(new CustomEvent('fab-show'));
  }, []);

  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Android Chrome ретаргетит click при мутации DOM: тот же тап, что открыл модалку,
  // прилетает в бэкдроп и мгновенно её закрывает. Гасим закрытие в первые 350 мс.
  const openedAt = useRef(Date.now());
  const handleBackdrop = () => {
    if (Date.now() - openedAt.current < 350) return;
    onClose();
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/expenses/${params.expenseId}`);
        if (!cancelled) setData(r);
      } catch { /* нет доступа/удалён — покажем без фото */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [params.expenseId]);

  const recognized = t('notifications.evt.demo_expense_offer_body', {
    amount: params.amount,
    currency: params.currency || 'AED',
    vendor: params.vendor || '',
    category: params.category || '',
  });

  return (
    <div
      onClick={handleBackdrop}
      style={{
        position: 'fixed', inset: 0, zIndex: 10010, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg2)', borderRadius: 16, maxWidth: 440, width: '100%',
          maxHeight: '88vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
          boxShadow: '0 16px 56px rgba(0,0,0,0.32)',
        }}
      >
        {/* Шапка — закреплена, не скроллится */}
        <div style={{ flexShrink: 0, padding: '20px 20px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>
              {t('notifications.evt.demo_expense_offer_title')}
            </div>
            <button
              onClick={onClose}
              aria-label="close"
              style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text2)', cursor: 'pointer', lineHeight: 1 }}
            >✕</button>
          </div>

          {(params.workerName || data?.title) && (
            <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 4 }}>
              {params.workerName ? `${t('notifications.expenseFrom')}: ${params.workerName}` : ''}
              {data?.title ? ` · ${data.title}` : ''}
            </div>
          )}
        </div>

        {/* Фото чека может быть длинным (вытянутый чек) — скроллится только эта часть,
            кнопки Согласовать/Отклонить остаются в досягаемости (sticky footer). */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '14px 20px 0' }}>
          <div style={{
            borderRadius: 12, overflow: 'hidden', background: 'var(--bg2, #f5f5f7)',
            minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {loading ? (
              <span style={{ color: 'var(--text3)', fontSize: 13, padding: 24 }}>…</span>
            ) : data?.receipt_url ? (
              <img src={data.receipt_url} alt={t('expenses.receipt')} style={{ width: '100%', height: 'auto', display: 'block' }} />
            ) : (
              <span style={{ color: 'var(--text3)', fontSize: 13, padding: 24 }}>{t('expenses.receipt')}</span>
            )}
          </div>

          <div style={{
            marginTop: 14, padding: '10px 12px', borderRadius: 10,
            background: 'rgba(79,142,247,0.12)', fontSize: 14, color: 'var(--text)',
          }}>
            {recognized}
          </div>
        </div>

        {/* Футер — закреплён, всегда в досягаемости */}
        <div style={{ flexShrink: 0, display: 'flex', gap: 10, padding: '16px 20px 18px' }}>
          <button
            disabled={busy}
            onClick={onApprove}
            style={{ flex: 1, background: '#22c55e', color: '#fff', border: 'none', padding: '12px', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}
          >{busy ? '…' : t('notifications.expenseAccept')}</button>
          <button
            disabled={busy}
            onClick={onReject}
            style={{ flex: 1, background: 'none', color: '#ef4444', border: '1px solid #ef444455', padding: '12px', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}
          >{t('notifications.expenseReject')}</button>
        </div>
      </div>
    </div>
  );
}
